import type {
  HealthQuestionnaireAnswer,
  HealthQuestionnaireResponse,
  Patient,
} from "../types";

const MULTI_SELECT_IDS = [
  "symptoms",
  "health_conditions",
  "medications",
  "doctor_diagnosed_conditions",
  "diabetes_medications",
  "substance_consumptions",
];

function formatValue(val: string): string {
  if (!val) return "";
  const noUnderscore = val.replace(/_/g, " ");
  return noUnderscore.charAt(0).toUpperCase() + noUnderscore.slice(1);
}

function collectAnswersForQuestion(
  responses: HealthQuestionnaireResponse[] | undefined,
  questionId: string
): HealthQuestionnaireAnswer[] {
  const found: HealthQuestionnaireAnswer[] = [];
  if (!responses) return found;
  for (let i = responses.length - 1; i >= 0; i--) {
    const entry = responses[i];
    const matches = entry.answers?.filter((a) => a.questionId === questionId);
    if (matches?.length) {
      found.push(...matches);
      break;
    }
  }
  return found;
}

export function getQuestionnaireList(
  patient: Patient | null,
  questionId: string
): string {
  const answers = collectAnswersForQuestion(
    patient?.healthQuestionnaireResponse,
    questionId
  );
  if (!answers.length) return "";

  if (MULTI_SELECT_IDS.includes(questionId)) {
    let values: string[] = [];
    answers.forEach((a) => {
      if (Array.isArray(a.data)) {
        values = values.concat(a.data.map((d) => formatValue(d.value)));
      } else if (a.value) {
        values.push(formatValue(a.value));
      }
    });
    const result = values.filter((v) => v.toLowerCase() !== "none").join(", ");
    if (result.toLowerCase() === "none") return "";
    return result;
  }

  return answers.map((a) => formatValue(a.value)).join(", ");
}

export function getQuestionnaireValue(
  patient: Patient | null,
  questionId: string
): string {
  const answers = collectAnswersForQuestion(
    patient?.healthQuestionnaireResponse,
    questionId
  );
  if (!answers.length) return "";

  const answer = answers[0];
  if (questionId === "diabetes") {
    const diabetesType = answer?.value;
    if (diabetesType === "type_1" || diabetesType === "type1")
      return "Diabetes type 1";
    if (diabetesType === "type_2" || diabetesType === "type2")
      return "Diabetes type 2";
    if (diabetesType === "yes") return "Diabetes";
    return "";
  }
  if (questionId === "diabetes_type") {
    const t = answer?.value;
    if (t === "type_1") return "Diabetes type 1";
    if (t === "type_2") return "Diabetes type 2";
    return formatValue(t || "");
  }
  return answer?.value ? formatValue(answer.value) : "";
}

export function getHealthConditionsDisplay(patient: Patient | null): string {
  const parts: string[] = [];
  const fromLegacy = getQuestionnaireList(patient, "health_conditions");
  const fromNew = getQuestionnaireList(patient, "doctor_diagnosed_conditions");
  if (fromLegacy) parts.push(fromLegacy);
  if (fromNew && fromNew !== fromLegacy) parts.push(fromNew);
  const diabetes =
    getQuestionnaireValue(patient, "diabetes") ||
    getQuestionnaireValue(patient, "diabetes_type");
  if (diabetes && !parts.join(", ").toLowerCase().includes("diabetes")) {
    parts.push(diabetes);
  }
  return parts.join(", ");
}

export function getMedicationsDisplay(patient: Patient | null): string {
  const primary = getQuestionnaireList(patient, "medications");
  if (primary) return primary;
  return getQuestionnaireList(patient, "diabetes_medications");
}

export function getFamilyHeartHistoryDisplay(patient: Patient | null): string {
  const value =
    getQuestionnaireValue(patient, "family_history") ||
    getQuestionnaireValue(patient, "family_history_heart_disease");
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

export function getLifestyleDisplay(patient: Patient | null): string {
  const items: string[] = [];

  const alcohol = getQuestionnaireValue(patient, "alcohol");
  if (
    alcohol &&
    !["no", "none"].includes(alcohol.toLowerCase()) &&
    (alcohol.toLowerCase().includes("yes") ||
      alcohol.toLowerCase().includes("regular") ||
      alcohol.toLowerCase().includes("occasional") ||
      alcohol.toLowerCase().includes("rarely"))
  ) {
    items.push("Alcohol use");
  }

  const alcoholUse = getQuestionnaireValue(patient, "alcohol_use");
  if (alcoholUse === "Occasionally" || alcoholUse === "Regularly") {
    if (!items.includes("Alcohol use")) items.push("Alcohol use");
  }

  const smoke = getQuestionnaireValue(patient, "smoke");
  if (
    smoke &&
    smoke.toLowerCase() !== "non smoker" &&
    !smoke.toLowerCase().includes("non_smoker") &&
    smoke.toLowerCase() !== "none"
  ) {
    if (
      smoke.toLowerCase().includes("smoker") ||
      smoke.toLowerCase().includes("light") ||
      smoke.toLowerCase().includes("moderate") ||
      smoke.toLowerCase().includes("heavy") ||
      smoke.toLowerCase().includes("ex")
    ) {
      items.push("Smoking");
    }
  }

  const smokingStatus = getQuestionnaireValue(patient, "smoking_status");
  if (smokingStatus === "Current" || smokingStatus === "Former") {
    if (!items.includes("Smoking")) items.push("Smoking");
  }

  const substances = getQuestionnaireList(patient, "substance_consumptions");
  if (substances) {
    substances.split(", ").forEach((s) => {
      if (s && !items.includes(s)) items.push(s);
    });
  }

  return items.join(", ");
}
